import { useState } from "react";
import { useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Bar } from "react-chartjs-2";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import "chart.js/auto";

import constants from "../../assets/constants";
import { extractAnalyticsDateLabels, getGraphColor } from "./graphUtils";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const BotpressUseageGraph = ({
  records,
  botpressGraphType = constants.botpressGraphTypes.usersGraph,
  colorIndex = 0,
}) => {
  const theme = useTheme();

  const generateGraphData = (analyticsRecords, graphType) => {
    if (analyticsRecords == null) {
      return null;
    }

    //  Default data structure
    //  const emptyData = {
    //    startDateTimeUtc: searchDate.toISOString(),
    //    endDateTimeUtc: searchDate.toISOString(),
    //    returningUsers: 0,
    //    newUsers: 0,
    //    sessions: 0,
    //    userMessages: 0,
    //    botMessages: 0,
    //    events: 0,
    //    eventTypes: {
    //      "webchat:trigger": 0,
    //      "webchat:conversationStarted": 0,
    //      state_expired: 0,
    //    },
    //    customEvents: {},
    //    llm: {
    //      calls: 0,
    //      errors: 0,
    //      inputTokens: 0,
    //      outputTokens: 0,
    //      latency: {
    //        mean: 0,
    //        sd: 0,
    //        min: 0,
    //        max: 0,
    //      },
    //      tokensPerSecond: {
    //        mean: 0,
    //        sd: 0,
    //        min: 0,
    //        max: 0,
    //      },
    //      cost: {
    //        sum: 0,
    //        mean: 0,
    //        sd: 0,
    //        min: 0,
    //        max: 0,
    //      },
    //    },
    //    messages: 0,
    //  };

    const labels = extractAnalyticsDateLabels(analyticsRecords);

    let dataSets = [];

    if (graphType === constants.botpressGraphTypes.usersGraph) {
      let x = colorIndex;
      dataSets.push({
        label: "Returning Users",
        data: analyticsRecords.map(record => record.returningUsers),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++, 0.3),
        borderWidth: 2,
        pointRadius: 3,
        fill: true,
        tension: 0.3,
      });
      dataSets.push({
        label: "New Users",
        data: analyticsRecords.map(record => record.newUsers),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++, 0.3),
        borderWidth: 2,
        pointRadius: 3,
        fill: true,
        tension: 0.3,
      });
    } else if (graphType === constants.botpressGraphTypes.messagesGraph) {
      let x = colorIndex;
      dataSets.push({
        label: "User Messages",
        data: analyticsRecords.map(record => record.userMessages),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++),
      });
      dataSets.push({
        label: "Bot Messages",
        data: analyticsRecords.map(record => record.userMessages),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++),
      });
    } else if (graphType === constants.botpressGraphTypes.sessionsGraph) {
      let x = colorIndex;
      dataSets.push({
        label: "Sessions",
        data: analyticsRecords.map(record => record.sessions),
        borderColor: getGraphColor(theme, x),
        backgroundColor: getGraphColor(theme, x++),
      });
    }
    return {
      labels: labels,
      datasets: dataSets,
    };
  };

  const [data, setData] = useState(null);

  const generateOptions = graphType => {
    if (graphType == null) {
      return null;
    }

    switch (graphType) {
      case constants.botpressGraphTypes.usersGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      case constants.botpressGraphTypes.messagesGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        };
      }
      case constants.botpressGraphTypes.sessionsGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: false,
            },
          },
          scales: {
            y: {
              ticks: {
                // Enforces a step of 1, ensuring only integers are displayed
                stepSize: 1,
                // Alternately, use a callback function to only return whole numbers
                // callback: function(value, index, ticks) {
                //   if (Math.floor(value) === value) {
                //     return value;
                //   }
                // }
              },
            },
          },
        };
      }
      default:
    }
  };

  const [options, setOptions] = useState(null);

  useEffect(() => {
    setData(generateGraphData(records, botpressGraphType));
    setOptions(generateOptions(botpressGraphType));
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, botpressGraphType]);

  if (!data || !options) {
    return <Box sx={{ position: "relative" }}>No data available</Box>;
  }

  if (botpressGraphType === constants.botpressGraphTypes.usersGraph) {
    return <Line options={options} data={data} />;
  } else {
    return <Bar options={options} data={data} />;
  }
};

export default BotpressUseageGraph;
